import React, { useState, useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Mountain, Trash2, Droplets, PieChart as PieIcon, Minimize2, Maximize2, HelpCircle, X, TrendingUp, Lock, Wallet, List } from 'lucide-react'; 
import { Card } from '../ui/Card';
import { InputGroup } from '../ui/InputGroup';
import { MasterTipsCard } from '../ui/MasterTipsCard';
import { formatCurrency, normalizeToMonthly } from '../../utils/finance';
import { THEMES, CHART_COLORS, ASSET_COLORS } from '../../utils/constants';

const CustomChartTooltip = ({ active, payload, label, dataAssets }) => {
    if (active && payload && payload.length) {
        const age = payload[0].payload.age;
        return (
            <div className="bg-slate-900 border border-slate-700 p-3 rounded shadow-xl z-50">
                <p className="text-white font-bold mb-2 border-b border-slate-800 pb-1">Age {age} ({label})</p>
                {payload.map((p, i) => {
                    const assetId = parseInt(p.name.replace('asset_', ''));
                    const assetName = !isNaN(assetId) ? dataAssets.find(a => a.id === assetId)?.name : p.name;
                    return (
                        <div key={i} className="flex items-center gap-2 text-xs mb-1" style={{ color: p.color }}>
                            <span>{assetName || p.name}:</span>
                            <span className="font-mono">{formatCurrency(p.value)}</span>
                        </div>
                    );
                })}
            </div>
        );
    }
    return null;
};

export const AssetsTab = ({ data, actions, calculations }) => {
    const [selectedAssetId, setSelectedAssetId] = useState(null);
    const [isCondensed, setIsCondensed] = useState(false); 
    const { projections, globalAllocation = [] } = calculations;
    
    const totalAssets = data.assets.reduce((sum, a) => sum + (parseFloat(a.value) || 0), 0);
    
    // Calculate Liquid vs Locked for the Top Card
    const liquidTypes = ['Checking', 'Savings', 'Cash', 'Brokerage', 'Crypto'];
    const liquidTotal = data.assets.filter(a => liquidTypes.includes(a.subtype)).reduce((sum, a) => sum + (parseFloat(a.value)||0), 0);
    const lockedTotal = totalAssets - liquidTotal;
    const liquidPercent = totalAssets > 0 ? (liquidTotal / totalAssets) * 100 : 0;

    const maxDomain = useMemo(() => {
        if (!projections || projections.length === 0) return 'auto';
        const maxVal = Math.max(...projections.map(p => p.netWorth));
        return maxVal * 1.1; 
    }, [projections]);

    const getAssetFlows = (assetId) => { 
        const flows = []; 
        data.deductions.filter(d => d.linkedAssetId === assetId).forEach(d => flows.push({ source: `Paycheck (${d.name})`, amount: d.type === 'fixed' ? d.amount : (data.salary/data.payFrequency)*(d.amount/100) })); 
        data.expenseItems.filter(e => e.linkedAssetId === assetId).forEach(e => flows.push({ source: `Burn (${e.name})`, amount: normalizeToMonthly(e.amount, e.frequency) })); 
        data.waterfall.filter(w => w.linkedAssetId === assetId).forEach(w => flows.push({ source: `Waterfall (${w.name})`, amount: "Surplus" })); 
        return flows; 
    };

    return (
        <div className="animate-fade-in">
            
            {/* --- MASTER TIPS CARD (Replaces Header & TabInfo) --- */}
            <MasterTipsCard 
                title="Build Your Foundation" 
                subtitle="Building the foundation of growing capital" 
                element="earth"
            >
                Earth represents stability. List your Assets here (Bank Accounts, Investments, Property) to build your Net Worth foundation. 
                Keep these updated monthly to ensure your Air (Vision) projections remain accurate.
            </MasterTipsCard>

            <div className="grid grid-cols-1 gap-6">
                
                {/* 1. VAULT STATUS (Top Analysis) */}
                <Card element="earth" className="bg-gradient-to-r from-emerald-900/40 to-slate-900 border-emerald-500/20">
                    <div className="flex flex-col md:flex-row justify-between items-end mb-4 gap-6">
                        <div>
                            <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                <Mountain className="w-6 h-6 text-emerald-400" /> Total Asset Vault
                            </h3>
                            <p className="text-sm text-slate-400">Total Net Value of all accounts</p>
                        </div>
                        <div className="text-right">
                            <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Current Value</span>
                            <p className="text-4xl font-bold text-white cursor-pointer hover:text-emerald-300 transition-colors" onClick={() => setSelectedAssetId(null)} title="Click to view Total Growth">{formatCurrency(totalAssets)}</p>
                        </div>
                    </div>

                    {/* Liquidity Bar */}
                    <div className="h-4 w-full bg-slate-950 rounded-full border border-slate-800 overflow-hidden relative mb-2 flex">
                        <div 
                            className="h-full bg-blue-500 hover:bg-blue-400 transition-all duration-1000 ease-out relative"
                            style={{ width: `${liquidPercent}%` }}
                            title={`Liquid: ${formatCurrency(liquidTotal)}`}
                        ></div>
                        <div className="h-full bg-emerald-600 hover:bg-emerald-500 transition-all flex-1" title={`Locked/Illiquid: ${formatCurrency(lockedTotal)}`}></div>
                    </div>
                    <div className="flex justify-between text-[10px] uppercase font-bold text-slate-500">
                        <div className="flex items-center gap-1"><Wallet className="w-3 h-3 text-blue-500"/> Liquid ({Math.round(liquidPercent)}%)</div>
                        <div className="flex items-center gap-1">Locked ({Math.round(100 - liquidPercent)}%) <Lock className="w-3 h-3 text-emerald-600"/></div>
                    </div>
                </Card>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    
                    {/* LEFT COLUMN: ASSET LIST */}
                    <div className="lg:col-span-8 space-y-4">
                        <div className="flex justify-between items-center">
                            <h3 className="font-bold text-white flex items-center gap-2"><List className="w-5 h-5 text-emerald-400"/> Your Accounts</h3>
                            <button onClick={() => setIsCondensed(!isCondensed)} className="text-xs text-slate-500 hover:text-white flex items-center gap-1">
                                {isCondensed ? <Maximize2 className="w-3 h-3" /> : <Minimize2 className="w-3 h-3" />} {isCondensed ? "Expand" : "Compact"}
                            </button>
                        </div>

                        <div className="space-y-2">
                            {data.assets.map((asset, index) => {
                                const isSimpleAsset = ['Checking', 'Savings'].includes(asset.subtype || "Brokerage");
                                const isSelected = selectedAssetId === asset.id;

                                return (
                                    <div 
                                        key={asset.id} 
                                        onClick={() => setSelectedAssetId(prev => prev === asset.id ? null : asset.id)}
                                        className={`group relative bg-slate-950 border rounded-xl transition-all cursor-pointer overflow-hidden ${isSelected ? 'border-emerald-500 ring-1 ring-emerald-500/50' : 'border-slate-800 hover:border-emerald-500/30'}`}
                                    >
                                        {/* Color Strip */}
                                        <div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: CHART_COLORS.earth[index % CHART_COLORS.earth.length] }}></div>
                                        
                                        <div className="p-3 pl-5">
                                            {/* DESKTOP ROW */}
                                            <div className="hidden md:flex items-center gap-4">
                                                
                                                {/* Name & Type */}
                                                <div className="flex-1 min-w-0">
                                                    <input 
                                                        value={asset.name} 
                                                        onChange={(e) => actions.updateArrayItem('assets', asset.id, 'name', e.target.value)} 
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="bg-transparent font-bold text-white outline-none w-full truncate placeholder-slate-600 hover:text-emerald-300 transition-colors" 
                                                        placeholder="Asset Name"
                                                    />
                                                    <div className="flex items-center gap-2 mt-0.5">
                                                        <select 
                                                            onClick={(e) => e.stopPropagation()} 
                                                            value={asset.subtype || "Brokerage"} 
                                                            onChange={(e) => actions.updateArrayItem('assets', asset.id, 'subtype', e.target.value)} 
                                                            className="bg-transparent text-[10px] text-slate-500 outline-none hover:text-white cursor-pointer uppercase tracking-wider font-bold"
                                                        >
                                                            <option value="Checking">Checking</option>
                                                            <option value="Savings">Savings</option>
                                                            <option value="Brokerage">Brokerage</option>
                                                            <option value="Retirement">Retirement</option>
                                                            <option value="Real Estate">Real Estate</option>
                                                            <option value="Crypto">Crypto</option>
                                                            <option value="Other">Other</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                {/* Balance Input */}
                                                <div className="w-32 text-right" onClick={(e) => e.stopPropagation()}>
                                                    <div className="relative">
                                                        <span className="absolute left-0 top-1 text-slate-500 text-xs">$</span>
                                                        <input 
                                                            type="number" 
                                                            value={asset.value} 
                                                            onChange={(v) => actions.updateArrayItem('assets', asset.id, 'value', parseFloat(v.target.value))} 
                                                            className="bg-transparent w-full text-right font-mono font-bold text-white outline-none focus:border-b focus:border-emerald-500" 
                                                        />
                                                    </div>
                                                </div>

                                                {/* Return Rate */}
                                                <div className="w-16 text-right" onClick={(e) => e.stopPropagation()}>
                                                    <div className="relative">
                                                        <input 
                                                            type="number" 
                                                            value={asset.returnRate} 
                                                            onChange={(v) => actions.updateArrayItem('assets', asset.id, 'returnRate', parseFloat(v.target.value))} 
                                                            className={`bg-slate-900/50 rounded px-1 w-full text-center text-xs font-bold outline-none ${asset.returnRate > 0 ? 'text-emerald-400' : 'text-slate-500'}`} 
                                                        />
                                                        <span className="absolute -right-2 top-0 text-[9px] text-slate-600">%</span>
                                                    </div>
                                                </div>

                                                {/* Delete */}
                                                <button onClick={(e) => { e.stopPropagation(); actions.removeArrayItem('assets', asset.id); }} className="opacity-0 group-hover:opacity-100 transition-opacity p-2 text-slate-600 hover:text-red-400">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>

                                            {/* MOBILE STACK */}
                                            <div className="md:hidden space-y-3">
                                                <div className="flex justify-between items-start">
                                                    <input 
                                                        value={asset.name} 
                                                        onChange={(e) => actions.updateArrayItem('assets', asset.id, 'name', e.target.value)} 
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="bg-transparent font-bold text-lg text-white outline-none w-full" 
                                                    />
                                                    <button onClick={(e) => { e.stopPropagation(); actions.removeArrayItem('assets', asset.id); }}><Trash2 className="w-4 h-4 text-slate-600 hover:text-red-400"/></button>
                                                </div>
                                                <div className="flex gap-2">
                                                    <div className="relative flex-1" onClick={(e) => e.stopPropagation()}>
                                                        <span className="absolute left-2 top-2 text-slate-500 text-xs">$</span>
                                                        <input 
                                                            type="number" 
                                                            value={asset.value} 
                                                            onChange={(v) => actions.updateArrayItem('assets', asset.id, 'value', parseFloat(v.target.value))} 
                                                            className="bg-slate-900 w-full rounded p-2 pl-5 text-white font-mono font-bold outline-none" 
                                                        />
                                                    </div>
                                                    <div className="w-20" onClick={(e) => e.stopPropagation()}>
                                                        <input 
                                                            type="number" 
                                                            value={asset.returnRate} 
                                                            onChange={(v) => actions.updateArrayItem('assets', asset.id, 'returnRate', parseFloat(v.target.value))} 
                                                            className="bg-slate-900 w-full rounded p-2 text-center text-emerald-400 font-bold outline-none" 
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* EXPANDED DETAILS (Allocation & Flows) */}
                                            {(!isCondensed || isSelected) && (
                                                <div className="mt-3 pt-3 border-t border-slate-800/50 flex flex-col md:flex-row gap-4 items-start animate-fade-in" onClick={(e) => e.stopPropagation()}>
                                                    
                                                    {/* Allocation Mini-Grid */}
                                                    {!isSimpleAsset && (
                                                        <div className="flex-1 w-full bg-slate-900/50 rounded-lg p-2 border border-slate-800">
                                                            <div className="grid grid-cols-6 gap-1">
                                                                {['usStock', 'intlStock', 'bonds', 'cash', 'crypto', 'realEstate'].map(type => (
                                                                    <div key={type} className="text-center">
                                                                        <label className="block text-[8px] text-slate-500 uppercase truncate">{type.replace(/([A-Z])/g, '$1').trim().substr(0,4)}</label>
                                                                        <input type="number" value={asset.allocation?.[type] || 0} onChange={(e) => actions.updateAssetAlloc(asset.id, type, e.target.value)} className="w-full bg-transparent text-[10px] text-center text-slate-300 outline-none border-b border-transparent focus:border-emerald-500" placeholder="0" />
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Connected Flows */}
                                                    <div className="flex flex-wrap gap-1 items-center">
                                                        {getAssetFlows(asset.id).length > 0 ? getAssetFlows(asset.id).map((flow, i) => (
                                                            <span key={i} className="px-2 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded text-[9px] text-emerald-400 flex items-center gap-1 whitespace-nowrap">
                                                                <Droplets className="w-2 h-2" /> {flow.source}
                                                            </span>
                                                        )) : <span className="text-[10px] text-slate-600 italic">No linked inflows</span>}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            
                            <button 
                                id="add-asset-btn"
                                onClick={() => actions.addArrayItem('assets', {id: Date.now(), name: "New Asset", subtype: "Checking", value: 0, returnRate: 7, volatility: 10, allocation: {}})} 
                                className="w-full py-3 border-2 border-dashed border-slate-800 rounded-xl text-slate-500 hover:text-emerald-400 hover:border-emerald-500/30 hover:bg-emerald-500/10 transition-all flex items-center justify-center gap-2 font-bold text-sm"
                            >
                                <Mountain className="w-4 h-4" /> Add Foundation Block
                            </button>
                        </div>
                    </div>
                    
                    {/* RIGHT COLUMN: CHARTS */}
                    <div className="lg:col-span-4 space-y-4">
                          <Card element="earth" className="h-[300px] flex flex-col">
                            <h3 className="text-sm font-bold text-emerald-300 mb-2 flex items-center gap-2"><PieIcon className="w-4 h-4"/> Global Allocation</h3>
                            <div className="flex-1 min-h-0">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={globalAllocation} innerRadius={50} outerRadius={70} paddingAngle={2} dataKey="value">
                                                {globalAllocation.map((entry, index) => <Cell key={`cell-${index}`} fill={ASSET_COLORS[index % ASSET_COLORS.length]} stroke="#022c22" />)}
                                        </Pie>
                                        <RechartsTooltip formatter={(v) => formatCurrency(v)} contentStyle={{backgroundColor: '#0f172a', borderColor: '#10b981', color: '#fff'}} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="flex flex-wrap gap-2 justify-center mt-2">
                                {globalAllocation.map((item, i) => (
                                    <div key={item.name} className="flex items-center gap-1 text-[10px] text-slate-400">
                                            <div className="w-2 h-2 rounded-full" style={{backgroundColor: ASSET_COLORS[i % ASSET_COLORS.length]}}></div>
                                            {item.name}
                                    </div>
                                ))}
                            </div>
                        </Card>

                          <Card element="earth" className="h-[300px] border-emerald-500/20 sticky top-24 flex flex-col">
                              <div className="flex justify-between items-center mb-2">
                                  <h3 className="text-sm font-bold text-emerald-300 flex items-center gap-2">
                                      <TrendingUp className="w-4 h-4"/> 
                                      {selectedAssetId ? `${data.assets.find(a => a.id === selectedAssetId)?.name} Growth` : "Total Projection"}
                                  </h3>
                                  {selectedAssetId && (
                                      <button onClick={() => setSelectedAssetId(null)} className="text-xs text-slate-500 hover:text-white flex items-center gap-1 transition-colors">
                                          Total View <X className="w-3 h-3" />
                                      </button>
                                  )}
                              </div>
                              <div className="flex-1 min-h-0">
                                  <ResponsiveContainer width="100%" height="100%">
                                      <AreaChart data={projections}>
                                          <defs><linearGradient id="colorEarth" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={THEMES.earth.hex} stopOpacity={0.4}/><stop offset="95%" stopColor={THEMES.earth.hex} stopOpacity={0}/></linearGradient></defs>
                                          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                                          <XAxis dataKey="year" stroke="#475569" tick={{fontSize: 10}} />
                                          <YAxis stroke="#475569" domain={[0, maxDomain]} tickFormatter={v => `$${v/1000}k`} tick={{fontSize: 10}} width={40} />
                                          <RechartsTooltip content={<CustomChartTooltip dataAssets={data.assets} />} />
                                          {selectedAssetId ? (<Area type="monotone" dataKey={`asset_${selectedAssetId}`} stroke={THEMES.earth.hex} fill="url(#colorEarth)" />) : (<Area type="monotone" dataKey="netWorth" stroke={THEMES.earth.hex} fill="url(#colorEarth)" />)}
                                      </AreaChart>
                                  </ResponsiveContainer>
                              </div>
                          </Card>
                    </div>
                </div>
            </div>
        </div>
    );
};